import { createSelector } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
// Types
import Reducers from "types/Reducers";
import QuestionTypes from "types/QuestionTypes";
import QuizIntegrations from "types/QuizIntegrations";
import PageTypes from "types/PageTypes";
// Model
import { RootState } from "store";
import IQuiz from "models/Quiz";
import IQuestion from "models/Question";
import IAnswer from "models/Answer";
import IPage from "models/Page";
// Selectors
import { selectPage, selectPages } from "store/pages/pagesSelectors";

dayjs.extend(isBetween);

export const selectQuiz = (state:RootState) => state[Reducers.Quiz].quiz;
export const selectQuestions = (state:RootState) => state[Reducers.Quiz].questions;
export const selectAdditionalAnswers = (state:RootState) => state[Reducers.Quiz].additionalAnswers;
export const selectAnswersOptions = (state:RootState) => state[Reducers.Quiz].answersOptions;

export const selectSortedQuestions = createSelector(
  [
    selectPages,
    selectQuestions
  ],
  (pages, questions) => {
    const questionsPages = pages.filter(page => page.type === PageTypes.Question);
    return questionsPages.map(page => questions.find(q => page.refs.includes(q.ref)));
  }
)

export const selectIsQuizStarted = createSelector(
  [ selectQuiz ],
  (quiz:IQuiz | null) => {
    if ( !quiz ) return false;
    if ( !quiz.startsDate ) return undefined;

    const now = dayjs();
    const startDate = dayjs(Number(quiz.startsDate));
    const endDate = quiz.endsDate ? dayjs(Number(quiz.endsDate)) : '';

    return !endDate
      ? now.isAfter(startDate)
      : now.isBetween(startDate, endDate)
    ;
  }
);

export const selectIsQuizEnded = createSelector(
  [ selectQuiz ],
  (quiz:IQuiz) => {
    if ( !quiz ) return false;
    if ( !quiz.endsDate ) return false;

    const now = dayjs();
    const endDate = dayjs(Number(quiz.endsDate));

    return now.isAfter(endDate);
  }
);

export const selectQuestionsLength = createSelector(
  [ selectPages ],
  (pages:IPage[]) => pages.filter(page => page.type === PageTypes.Question).length
);

export const selectQuestion = createSelector(
  [
    selectQuestions,
    selectPage
  ],
  (questions:IQuestion[], page) => questions.find(question => question.ref === page?.refs[0]) || {} as IQuestion
);

export const selectQuestionIndex = createSelector(
  [
    selectPages,
    selectQuestion
  ],
  (pages:IPage[], question) => {
    const questionPages = pages.filter(page => page.type === PageTypes.Question);
    return questionPages.findIndex(page => page.refs.includes(question.ref)) + 1;
  }
);

export const selectNextQuestion = createSelector(
  [
    selectPages,
    selectQuestions,
    selectQuestionIndex,
  ],
  (pages:IPage[], questions:IQuestion[], questionIndex) => {
    const questionPages = pages.filter(page => page.type === PageTypes.Question);
    const nextQuestionPage = questionPages[questionIndex];
    if (!nextQuestionPage) return null;
    return questions.find(question => nextQuestionPage.refs.includes(question.ref));
  }
);

export const selectAdditionalQuestions = createSelector(
  [
    selectQuiz,
    selectQuestions,
    selectPage
  ],
  (quiz:IQuiz | null, questions:IQuestion[], page) => {
    if ( !quiz ) return {};
    const additionalQuestions = questions.filter(question => page.refs.includes(question.ref));
    return additionalQuestions.reduce((acc:any, cur:IQuestion) => {
      const question = { title: cur.title, type: cur.type, renderAsDropdown: cur.renderAsDropdown || false, image: cur.image };
      acc[cur.ref] = cur.type === QuestionTypes.ShortText
        ? { ...question, text: '', answerRef: uuidv4() }
        : {
            ...question,
            answers: cur.answers.reduce((a:any, c:IAnswer) => {
              a[c.ref] = { title: c.title, checked: false }
              return a;
            }, {})
          }
      ;
      return acc;
    }, {});
  }
)

export const selectQuizIntegrationConfig = createSelector(
  [
    selectQuiz,
    (_:any, props:{ integrationName:QuizIntegrations }) => props
  ],
  (quiz: IQuiz, { integrationName }) => {
    return quiz.integrations?.find(integration => integration.service === integrationName)?.roktConfig || null
  }
);
